import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  FlaskConical, Plus, Edit3, Trash2, ChevronRight, ChevronDown, X, AlertTriangle, Save, Languages,
} from 'lucide-react';

const LANGS = ['it', 'es', 'en'];

const emptyForm = () => ({
  category: 'isolation_test',
  title: { it: '', es: '', en: '' },
  description: { it: '', es: '', en: '' },
  safety_warnings: { it: '', es: '', en: '' },
  required_tools: { it: '', es: '', en: '' },
  expected_values: [],
  steps: [{ order: 1, it: '', es: '', en: '', expected_value: '', tool: '' }],
  tags: [],
});

export default function UniversalProceduresPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const lang = i18n.language || 'it';

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // procedure being edited or null
  const [form, setForm] = useState(emptyForm());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get('/universal-procedures').then((r) => setItems(r.data || [])), []);
  const loadCategories = useCallback(
    () => api.get('/universal-procedures/categories').then((r) => setCategories(r.data || [])),
    [],
  );
  useEffect(() => { load(); loadCategories(); }, [load, loadCategories]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const startNew = () => {
    setEditing('new');
    setForm(emptyForm());
  };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      category: p.category,
      title: { it: '', es: '', en: '', ...(p.title || {}) },
      description: { it: '', es: '', en: '', ...(p.description || {}) },
      safety_warnings: { it: '', es: '', en: '', ...(p.safety_warnings || {}) },
      required_tools: { it: '', es: '', en: '', ...(p.required_tools || {}) },
      expected_values: p.expected_values || [],
      steps: (p.steps || []).map((s) => ({
        order: s.order, it: s.it || '', es: s.es || '', en: s.en || '',
        expected_value: s.expected_value || '', tool: s.tool || '',
      })),
      tags: p.tags || [],
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(emptyForm()); };

  const addStep = () => setForm((f) => ({
    ...f,
    steps: [...f.steps, { order: f.steps.length + 1, it: '', es: '', en: '', expected_value: '', tool: '' }],
  }));
  const removeStep = (idx) => setForm((f) => ({ ...f, steps: f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })) }));

  const save = async () => {
    if (!form.title.it && !form.title.en && !form.title.es) { toast.error(t('app.required')); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/universal-procedures', form);
        toast.success(t('app.create'));
      } else {
        await api.put(`/universal-procedures/${editing}`, form);
        toast.success(t('app.edit_action') + ' ✓');
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/universal-procedures/${confirmDelete.id}`);
      toast.success(t('app.delete') + ' ✓');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  // Auto-translation: ask Claude to fill missing-language fields.
  // The backend infers source lang (preferring IT) and only writes missing langs unless
  // overwrite=true. Per-procedure spinner via translating={id}.
  const [translating, setTranslating] = useState(null);
  const autoTranslate = async (p, overwrite = false) => {
    setTranslating(p.id);
    try {
      const { data } = await api.post(`/universal-procedures/${p.id}/auto-translate`, { overwrite });
      setItems((arr) => arr.map((x) => x.id === data.id ? data : x));
      toast.success(t('universal.translated_ok') || 'Traduzione automatica completata');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (t('universal.translate_error') || 'Errore traduzione'));
    } finally { setTranslating(null); }
  };

  const missingLangs = (p) => LANGS.filter((lg) => !((p.title || {})[lg] || '').trim()
    && !((p.steps || []).some((s) => (s[lg] || '').trim())));

  // Re-run the backend seed of the 6 starter procedures. Idempotent server-side: if
  // procedures already exist this is a no-op. Used by admins on new/empty
  // environments (typical case: a deployment whose DB pre-existed the feature).
  const [seeding, setSeeding] = useState(false);
  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/universal-procedures/seed-defaults');
      toast.success((t('universal.seed_ok') || 'Procedure standard caricate') + ` (${data.total})`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (t('universal.seed_error') || 'Errore caricamento'));
    } finally { setSeeding(false); }
  };

  const grouped = items.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  const catLabel = (catValue) => {
    const c = categories.find((x) => x.value === catValue);
    return (c?.labels?.[lang] || c?.label) || catValue.replace(/_/g, ' ');
  };

  return (
    <div className="animate-fade-up" data-testid="universal-procedures-page">
      <div className="overline text-[#002FA7]">KNOWLEDGE BASE</div>
      <div className="flex items-start gap-3 mt-2">
        <FlaskConical className="w-9 h-9 text-[#002FA7] shrink-0 mt-1" strokeWidth={1.6} />
        <div className="flex-1">
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{t('universal.title')}</h1>
          <p className="text-gray-600 mt-2 max-w-3xl">{t('universal.subtitle')}</p>
          <p className="text-xs text-[#002FA7] mt-1 font-mono uppercase">{t('universal.brand_hint')}</p>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-6">
          {!editing && (
            <div className="flex flex-wrap gap-2">
              <button onClick={startNew} className="btn-primary" data-testid="up-new-btn">
                <Plus className="w-4 h-4" /> {t('universal.new')}
              </button>
              <button
                onClick={seedDefaults}
                disabled={seeding}
                className="btn-secondary"
                title={t('universal.seed_tooltip') || 'Carica le 6 procedure standard (isolamento, CAN bus, contattori, encoder, precarica, sicurezza HV). Sicuro: non sovrascrive se esistono già.'}
                data-testid="up-seed-defaults-btn"
              >
                <FlaskConical className="w-4 h-4" />
                {seeding ? '…' : (t('universal.seed_action') || 'Carica procedure standard')}
              </button>
            </div>
          )}
          {editing && (
            <div className="surface p-5 mt-3 space-y-4" data-testid="up-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-gray-600 block mb-1">{t('universal.cat')} *</label>
                  <select className="input-base" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="up-form-cat">
                    {categories.map((c) => <option key={c.value} value={c.value}>{c.labels?.[lang] || c.label}</option>)}
                  </select>
                </div>
              </div>
              {LANGS.map((l) => (
                <div key={l} className="border border-gray-200 p-3">
                  <div className="text-xs font-mono uppercase text-gray-500 mb-2">{l}</div>
                  <input className="input-base" placeholder={t('universal.title_field')}
                    value={form.title[l]} onChange={(e) => setForm({ ...form, title: { ...form.title, [l]: e.target.value } })} />
                  <textarea className="input-base mt-2" rows={2} placeholder={t('universal.description')}
                    value={form.description[l]} onChange={(e) => setForm({ ...form, description: { ...form.description, [l]: e.target.value } })} />
                  <textarea className="input-base mt-2" rows={2} placeholder={t('universal.safety')}
                    value={form.safety_warnings[l]} onChange={(e) => setForm({ ...form, safety_warnings: { ...form.safety_warnings, [l]: e.target.value } })} />
                  <input className="input-base mt-2" placeholder={t('universal.tools')}
                    value={form.required_tools[l]} onChange={(e) => setForm({ ...form, required_tools: { ...form.required_tools, [l]: e.target.value } })} />
                </div>
              ))}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="overline text-gray-600">{t('universal.steps')}</span>
                  <button type="button" onClick={addStep} className="btn-ghost text-xs"><Plus className="w-3 h-3" /> step</button>
                </div>
                <div className="space-y-2">
                  {form.steps.map((s, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 p-2" data-testid={`up-step-${idx}`}>
                      <div className="flex gap-2 items-center mb-2">
                        <span className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center text-xs font-mono">{s.order}</span>
                        <input className="input-base flex-1" placeholder={`${t('universal.step_action')} (IT)`}
                          value={s.it} onChange={(e) => setForm({ ...form, steps: form.steps.map((x, i) => i === idx ? { ...x, it: e.target.value } : x) })} />
                        <button type="button" onClick={() => removeStep(idx)} className="btn-ghost"><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input className="input-base" placeholder={`${t('universal.step_action')} (EN)`}
                          value={s.en} onChange={(e) => setForm({ ...form, steps: form.steps.map((x, i) => i === idx ? { ...x, en: e.target.value } : x) })} />
                        <input className="input-base" placeholder={`${t('universal.step_action')} (ES)`}
                          value={s.es} onChange={(e) => setForm({ ...form, steps: form.steps.map((x, i) => i === idx ? { ...x, es: e.target.value } : x) })} />
                        <input className="input-base" placeholder={t('universal.step_expected')}
                          value={s.expected_value} onChange={(e) => setForm({ ...form, steps: form.steps.map((x, i) => i === idx ? { ...x, expected_value: e.target.value } : x) })} />
                        <input className="input-base" placeholder={t('universal.step_tool')}
                          value={s.tool} onChange={(e) => setForm({ ...form, steps: form.steps.map((x, i) => i === idx ? { ...x, tool: e.target.value } : x) })} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={cancelEdit} className="btn-secondary">{t('app.cancel')}</button>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="up-save-btn">
                  <Save className="w-4 h-4" /> {saving ? '…' : t('universal.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 space-y-3">
        {items.length === 0 ? (
          <div className="surface p-12 text-center">
            <FlaskConical className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">{t('universal.no_procs')}</div>
            {isAdmin && (
              <button
                onClick={seedDefaults}
                disabled={seeding}
                className="btn-primary mt-4 mx-auto"
                data-testid="up-seed-defaults-empty-btn"
              >
                <FlaskConical className="w-4 h-4" />
                {seeding ? '…' : (t('universal.seed_action') || 'Carica procedure standard')}
              </button>
            )}
          </div>
        ) : Object.entries(grouped).map(([cat, procs]) => {
          const cid = `c:${cat}`;
          const open = !!expanded[cid];
          return (
            <div key={cat} className="surface overflow-hidden" data-testid={`up-cat-${cat}`}>
              <button onClick={() => toggle(cid)} className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 text-left">
                {open ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <FlaskConical className="w-4 h-4 text-[#002FA7]" />
                <span className="font-display font-bold uppercase tracking-wide">{catLabel(cat)}</span>
                <span className="ml-auto text-xs text-gray-500 font-mono">{procs.length}</span>
              </button>
              {open && (
                <div className="divide-y divide-gray-200">
                  {procs.map((p) => (
                    <div key={p.id} className="p-4" data-testid={`up-${p.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold">{(p.title || {})[lang] || (p.title || {}).it || (p.title || {}).en}</div>
                          {(p.description || {})[lang] && <div className="text-sm text-gray-600 mt-1">{(p.description || {})[lang]}</div>}
                        </div>
                        {isAdmin && (
                          <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                            {missingLangs(p).length > 0 && (
                              <button
                                onClick={() => autoTranslate(p, false)}
                                disabled={translating === p.id}
                                className="inline-flex items-center gap-1 bg-indigo-700 text-white px-2 py-1 text-xs font-semibold hover:bg-indigo-800 disabled:opacity-50"
                                title={(t('universal.translate_missing') || 'Traduci nelle lingue mancanti') + `: ${missingLangs(p).join(', ')}`}
                                data-testid={`up-translate-${p.id}`}
                              >
                                <Languages className="w-3.5 h-3.5" />
                                {translating === p.id ? '…' : (t('universal.translate_action') || 'Traduci')}
                                <span className="text-[10px] font-mono opacity-80">[{missingLangs(p).join('/')}]</span>
                              </button>
                            )}
                            {missingLangs(p).length === 0 && (
                              <button
                                onClick={() => {
                                  if (window.confirm(t('universal.translate_overwrite_confirm') || 'Tutte le lingue sono già compilate. Vuoi sovrascriverle con una nuova traduzione automatica?')) {
                                    autoTranslate(p, true);
                                  }
                                }}
                                disabled={translating === p.id}
                                className="inline-flex items-center gap-1 bg-white border border-indigo-300 text-indigo-700 px-2 py-1 text-xs font-semibold hover:bg-indigo-50 disabled:opacity-50"
                                title={t('universal.translate_overwrite') || 'Forza ri-traduzione di tutte le lingue'}
                                data-testid={`up-retranslate-${p.id}`}
                              >
                                <Languages className="w-3.5 h-3.5" />
                                {translating === p.id ? '…' : (t('universal.translate_overwrite_action') || 'Ri-traduci')}
                              </button>
                            )}
                            <button onClick={() => startEdit(p)} className="btn-ghost text-xs"><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setConfirmDelete(p)} className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 text-xs font-semibold hover:bg-red-700"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      {(p.safety_warnings || {})[lang] && (
                        <div className="mt-3 border-l-4 border-red-500 bg-red-50/40 p-2 text-xs">
                          <span className="overline text-red-700">⚠ {t('universal.safety')}:</span> {(p.safety_warnings || {})[lang]}
                        </div>
                      )}
                      {(p.required_tools || {})[lang] && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="overline text-gray-500">{t('universal.tools')}:</span> {(p.required_tools || {})[lang]}
                        </div>
                      )}
                      {(p.steps || []).length > 0 && (
                        <ol className="mt-3 space-y-1.5">
                          {p.steps.map((s) => {
                            const action = s[lang] || s.it || s.en || s.es || '';
                            return (
                              <li key={s.order} className="flex items-start gap-2 text-sm">
                                <span className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">{s.order}</span>
                                <div className="flex-1">
                                  <div className="text-gray-900">{action}</div>
                                  {s.expected_value && <div className="text-xs text-green-700 font-mono">→ {s.expected_value}</div>}
                                  {s.tool && <div className="text-[0.65rem] text-gray-500 font-mono">{t('universal.step_tool')}: {s.tool}</div>}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)} data-testid="up-confirm">
          <div className="bg-white border border-gray-200 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-1" />
              <div>
                <div className="font-display font-bold">{t('universal.delete_confirm')}</div>
                <div className="text-sm text-gray-600 mt-1">{(confirmDelete.title || {})[lang] || confirmDelete.category}</div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">{t('app.cancel')}</button>
              <button onClick={remove} className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 hover:bg-red-700">
                <Trash2 className="w-4 h-4" /> {t('app.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
